import { useQuery } from '@tanstack/react-query'
import { Avatar, Card, Flex, Skeleton, Typography } from 'antd'
import { ArrowRight, History } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'

import { organizationsAuditLogsListOptions } from 'src/api/main'
import { DateTimeCell } from 'src/components'
import { paths } from 'src/router'
import {
  SEVERITY_COLORS,
  getAuditActorName,
  getAuditEventCode,
  getInitials,
  humanizeAuditCode,
  normalizeAuditSeverity,
  toAuditAcceptLanguage,
  translateAuditAction
} from 'src/utils'

import { useStyles } from './styles'

const { Text } = Typography

const MAX_EVENTS = 6
const CORE_AUDIT_SERVICE = 'core'

type Props = {
  workspaceId: number
  workspaceIdParam: string
}

export const OverviewActivity = ({ workspaceId, workspaceIdParam }: Props) => {
  const { styles } = useStyles()
  const { t, i18n } = useTranslation()
  const language = i18n.resolvedLanguage ?? i18n.language

  const idValid = Number.isFinite(workspaceId) && workspaceId > 0

  const { data, isLoading } = useQuery({
    ...organizationsAuditLogsListOptions({
      path: { organization_id: workspaceId },
      query: { service: CORE_AUDIT_SERVICE, limit: MAX_EVENTS, offset: 0 },
      headers: { 'Accept-Language': toAuditAcceptLanguage(language) }
    }),
    enabled: idValid,
    // Opt out of the app-wide refetchOnMount:false so returning to the overview
    // shows the latest events instead of the ones cached on the first visit.
    refetchOnMount: 'always',
    // Audit rows are append-only and land constantly: also re-fetch when the user
    // comes back to the browser tab, since a mount-only refetch misses the case
    // where the action was performed elsewhere and this page stayed open.
    refetchOnWindowFocus: true
  })

  const recent = idValid ? (data?.results ?? []) : []

  return (
    <Card variant="outlined" className={styles.panelCard}>
      <Flex className={styles.panelHeader}>
        <Text className={styles.panelTitle}>
          {t('workspaceOverview.activity.title')}
        </Text>
        <Link
          to={paths.workspaces.audit(workspaceIdParam)}
          style={{
            fontSize: 12,
            display: 'inline-flex',
            alignItems: 'center',
            gap: 4
          }}
        >
          {t('workspaceOverview.activity.viewAll')}
          <ArrowRight size={12} />
        </Link>
      </Flex>

      {isLoading ? (
        <div style={{ padding: 14 }}>
          <Skeleton active paragraph={{ rows: 4 }} title={false} />
        </div>
      ) : recent.length === 0 ? (
        <div className={styles.activityEmpty}>
          <div className={styles.activityEmptyIcon} aria-hidden>
            <History size={20} />
          </div>
          <Text strong>{t('workspaceOverview.activity.emptyTitle')}</Text>
          <Text
            type="secondary"
            style={{ fontSize: 12, textAlign: 'center', maxWidth: 360 }}
          >
            {t('workspaceOverview.activity.emptyDescription')}
          </Text>
        </div>
      ) : (
        <Flex vertical>
          {recent.map((event) => {
            const actorName =
              getAuditActorName(event.actor) ||
              t('workspaceOverview.activity.systemActor')
            const severity = normalizeAuditSeverity(event.severity)
            const severityColor =
              (severity && SEVERITY_COLORS[severity]) ??
              'var(--ant-color-text-tertiary)'
            const actionCode = getAuditEventCode(event)
            const meta =
              event.description?.trim() ||
              (event.entity_type && event.entity_id
                ? `${humanizeAuditCode(event.entity_type)} #${event.entity_id}`
                : '')

            return (
              <div key={event.id} className={styles.activityRow}>
                <span
                  className={styles.activitySeverityDot}
                  style={{ background: severityColor }}
                  aria-hidden
                />
                <Avatar
                  size={26}
                  style={{
                    background: 'var(--ant-color-fill-secondary)',
                    color: 'var(--ant-color-text-secondary)',
                    fontSize: 10,
                    fontWeight: 600,
                    flex: '0 0 auto'
                  }}
                >
                  {getInitials(actorName) ||
                    actorName.slice(0, 2).toUpperCase() ||
                    '?'}
                </Avatar>
                <Flex vertical style={{ flex: 1, minWidth: 0 }} gap={2}>
                  <Flex align="center" gap={8} wrap="wrap">
                    <Text className={styles.activityActor} ellipsis>
                      {actorName}
                    </Text>
                    <span className={styles.activityAction}>
                      {translateAuditAction(t, actionCode)}
                    </span>
                  </Flex>
                  {meta ? (
                    <Text
                      className={styles.activityMeta}
                      ellipsis={{ tooltip: meta }}
                    >
                      {meta}
                    </Text>
                  ) : null}
                </Flex>
                <DateTimeCell
                  value={event.timestamp}
                  variant="relative"
                  style={{ fontSize: 11, flex: '0 0 auto' }}
                />
              </div>
            )
          })}
        </Flex>
      )}
    </Card>
  )
}
